import { useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, X, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";

export default function PhotoUpload({ photos = [], onChange, multiple = true, label = "Photos", testId = "photo-upload" }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    setUploading(true);
    try {
      const ids = [];
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(data.file_id);
      }
      onChange(multiple ? [...photos, ...ids] : ids.slice(0, 1));
    } catch (e) {
      toast.error("Upload failed. Only images up to 8MB.");
    } finally {
      setUploading(false);
    }
  };

  const remove = (id) => onChange(photos.filter((p) => p !== id));

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex flex-wrap gap-3">
        {photos.map((id) => (
          <div key={id} className="relative w-24 h-24 rounded-lg overflow-hidden border border-border group">
            <img src={fileUrl(id)} alt="upload" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(id)}
              data-testid={`${testId}-remove`}
              className="absolute top-1 right-1 bg-black/70 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid={`${testId}-add-button`}
          className="w-24 h-24 rounded-lg border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-primary transition-colors"
        >
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
          <span className="text-xs">{label}</span>
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={(e) => e.target.files?.length && handleFiles(e.target.files)}
      />
    </div>
  );
}
